import { getName } from "@home/utils/common";
import React, { useEffect, useRef, useState } from "react";
import { ConfigProvider } from "tea-component";
import { useFilterData } from "./_hooks";
import classNames from "classnames";
import Abnormal from "@home/components/Abnormal";
import { ICompetitorsSelectorProps } from "./_type";
import { useLocal } from "@/pages/dashboard/utils/customHook";
import Render from "@/pages/dashboard/components/PageContainer/Render";
import CompetitorsSelector from "./CompetitorsSelector";
import { BENCHMARK_RATE_DEFAULT, CHECKLIST_DEFAULT } from "../GTDRAssessmentFilter/VersionFilter/_constant";
import CommonCheckBox from "@/pages/dashboard/components/CommonCheckBox";
import { dynamicString } from "@home/utils/dynamicString";
import './index.scss';
import TipContext from "@home/components/TipContext";

/**
 * GTDRAssessmentFilter 组件用于渲染 GTDR 评估过滤器界面
 * @param props - 组件属性
 * @param props.children - 子组件
 * @param props.className - 自定义类名
 * @param props.style - 自定义样式
 * @param props.competitorsConfig - 版本筛选器配置
 * @param props.competitorsConfig.competitorsNameEn - 版本筛选器英文名称
 * @param props.competitorsConfig.competitorsNameCn - 版本筛选器中文名称
 * @param props.competitorsConfig.competitorsErrorTipsEn - 版本选择错误提示（英文）
 * @param props.competitorsConfig.competitorsErrorTipsCn - 版本选择错误提示（中文）
 * @param props.competitorsConfig.competitorsPlaceHolderCn - 版本提示信息（中文）
 * @param props.competitorsConfig.competitorsTipsEn - 版本提示信息（英文）
 * @param props.competitorsConfig.competitorsDefault - 默认选中的版本
 * @param props.competitorsConfig.minSelectedNum - 最少选择数量
 * @param props.api - API 配置
 * @returns 返回带有版本筛选功能的 GTDR 评估过滤器组件
 */
export default function StudioAssessmentFilter(props: ICompetitorsSelectorProps) {
  const { children, className, style, competitorsConfig, checkBoxConfig, data, subtitle, subtitleCn, tipEn, tipCn } = props;
  const {
    competitorsNameEn = 'Competitors',
    competitorsNameCn = '竞品',
    competitorsErrorTipsEn = 'Please select a version',
    competitorsErrorTipsCn = '请选择一个版本',
    competitorsPlaceHolderCn = '',
    competitorsPlaceHolderEn = '',
    minSelectedNum = 1,
    maxSelectedNum = 5,
  } = competitorsConfig || {};

  const {
    value: benchmarkLevelDefault,
    checkList = CHECKLIST_DEFAULT
  } = checkBoxConfig || {};

  const { isEN } = useLocal('filter');
  const tip = getName(isEN, tipEn, tipCn);

  // const [competitors, setcompetitors] = useState<string[]>([]);
  const [showBubble, setShowBubble] = useState<boolean>(false);
  const [benchmarkLevel, setBenchmarkLevel] = useState(benchmarkLevelDefault);

  const {
    loading,
    error,
    competitorsDefault,
    studioCode,
    getData,
    hasCompetitors,
  } = useFilterData();

  const [filterValue, setFilterValue] = useState<any>({
    competitors: competitorsDefault
  });

  // 添加filterValueRef 是因为当除非筛选器错误提示（少于最少选中数）的时候，不希望触发页面的接口重新请求
  const filterValueRef = useRef({
    competitors: competitorsDefault,
  });

  useEffect(() => {
    // 配置>代码计算的最新
    const competitorsDefaultVal = competitorsDefault || [];
    // setcompetitors(competitorsDefaultVal);
    setFilterValue({
      competitors: competitorsDefaultVal,
    });
    filterValueRef.current = {
      competitors: competitorsDefaultVal,
    };
    setBenchmarkLevel(benchmarkLevelDefault || BENCHMARK_RATE_DEFAULT);
  }, [JSON.stringify(competitorsDefault), benchmarkLevelDefault]);

  const handleOnChange = (val: string[]) => {
    setBenchmarkLevel(val);
  };

  /**
   * 处理确认按钮点击事件
   * 当选择的竞品数量满足最小要求时，更新过滤值
   * @private
   */
  const handleCofirm = (optionsValue: string[]) => {
    if (optionsValue?.length < minSelectedNum) {
      setShowBubble(true);
      setFilterValue({
        competitors: [],
      });
    } else {
      setShowBubble(false);
      setFilterValue({
        competitors: optionsValue,
      });
      filterValueRef.current = {
        competitors: optionsValue,
      };
    }
  };

  return (
    <ConfigProvider locale={isEN ? 'zh' : 'en'}>
      <div className={classNames('dbd-assessment-filter', className)} style={style ? style : {}}>
        <Abnormal
          isLoading={false}
          isError={error}
          classNames={"dbd-assessment-filter-abnormal"}
          // 733 为保持和外部loading高度一致（页面配置获取的loading）
          style={{ height: '36px', backgroundColor: '#fff', marginBottom: '20px' }}
          component={
            <div className='dbd-assessment-filter-box'>
              <CompetitorsSelector
                value={filterValue?.competitors || []}
                onConfirm={handleCofirm}
                maxSelected={maxSelectedNum}
                showBubble={showBubble}
                getData={getData}
                name={getName(isEN, competitorsNameEn, competitorsNameCn)}
                palaceHolder={getName(isEN, competitorsPlaceHolderEn, competitorsPlaceHolderCn)}
                bubbleContent={getName(isEN, competitorsErrorTipsEn, competitorsErrorTipsCn)}
                tip={tip}
              />
              {tip && <TipContext tipText={ tip } className={'dbd-assessment-filter-tip'} />}
              <CommonCheckBox
                className='dbd-assessment-filter-check'
                value={benchmarkLevel}
                checkList={checkList}
                onChange={handleOnChange} />
              <div className='dbd-assessment-filter-subtitle'>
                {subtitle && getName(isEN, dynamicString(subtitle,data), dynamicString(subtitleCn, data))}
              </div>
            </div>
          }
        />
        {
          children?.map((item: { props: {}; name: any; }, index: any) => {
            const { config, data, changeData } = item?.props || {};
            return <>
              <Render
                config={config}
                key={item?.name + index}
                data={data}
                changeData={changeData}
                filterValue={filterValueRef.current}
                benchmarkLevel={benchmarkLevel}
                checkList={checkList}
                filterLoading={loading}
                filterError={error}
                studioCode={studioCode}
                hasCompetitors={hasCompetitors}
              />
            </>;

          })
        }
      </div>
    </ConfigProvider>
  );
}
